/**
 * Outfit star ratings in the desktop gallery (owner, 2026-10-06: "we need a way for users to rate the outfits 1-5
 * stars"; docs/CONTRACT-day-outfits-and-ratings.md, "Star ratings"): what a refused rating means for the gallery, the
 * words for an outfit's average, and how the galleries of one client agree. No React, and nothing here leaves the page:
 * a person's stars are never logged, reported, put in telemetry or written to the console.
 */
import { MascotRatingError } from './client.js';
/** The apps gyro-svc takes a rating from: the desktop app and the Office pane. Any other app shows no stars. */
export const MASCOT_RATING_APPS = new Set(['desktop', 'office']);
/** The words for a 1 to 5 star choice: "1 star", "4 stars". */
export function starsText(stars) {
    return stars === 1 ? '1 star' : `${stars} stars`;
}
/**
 * An outfit's average in words: what the tile shows ("4.6 · 12 ratings"), what a screen reader hears ("Average 4.6 stars
 * from 12 ratings"), or, before anyone rated it, "No ratings yet" for both.
 */
export function ratingSummary(rating) {
    if (rating.average === null || rating.count === 0)
        return { shown: 'No ratings yet', spoken: 'No ratings yet' };
    const average = rating.average.toFixed(1);
    const count = rating.count === 1 ? '1 rating' : `${rating.count.toLocaleString('en-US')} ratings`;
    return { shown: `${average} · ${count}`, spoken: `Average ${average} stars from ${count}` };
}
/** gyro-svc's own wait when it gives none we can read: its limit is 20 a minute. */
const DEFAULT_WAIT_MS = 30_000;
/** A wait in words: "Try again in 12 seconds." */
export function ratingWaitMessage(waitMs) {
    const seconds = Math.max(1, Math.ceil(waitMs / 1000));
    return `Too many ratings at once. Try again in ${seconds === 1 ? '1 second' : `${seconds} seconds`}.`;
}
/**
 * The refusals the contract lists, each in plain words. A refusal gyro-svc answered changed nothing, so showing the stars
 * from before is exact; when no answer came (the network, the desktop forward's 502 or 504, an answer that is not a
 * rating's), the rating may have been saved, so the gallery reads itself again rather than guess.
 */
export function mascotRatingRefusal(error) {
    const code = error instanceof MascotRatingError ? error.code : 'network';
    const status = error instanceof MascotRatingError ? error.status : 0;
    if (status === 404 && code === 'mascot_outfit_not_found')
        return { message: 'That outfit is no longer available.', reload: true };
    // mascot_ratings_off, or a gyro-svc that has no rating route at all.
    if (status === 404)
        return { message: 'Outfit ratings are switched off.', hide: true };
    if (status === 409)
        return { message: 'Only outfits in today’s gallery can get a first rating.', reload: true };
    if (status === 429) {
        const waitMs = error instanceof MascotRatingError && error.retryAfterMs !== undefined && error.retryAfterMs > 0 ? error.retryAfterMs : DEFAULT_WAIT_MS;
        return { message: ratingWaitMessage(waitMs), waitMs };
    }
    if (status === 503 && code === 'mascot_ratings_full')
        return { message: 'Gyro can’t keep any more ratings just now, so yours wasn’t saved.' };
    if (status === 503)
        return { message: 'Your stars couldn’t be saved just now. Try again in a moment.' };
    if (status === 401)
        return { message: 'Gyro needs you to sign in again. Ask Gyro something to sign in, then rate the outfit.' };
    if (status === 403 && code === 'app_disabled')
        return { message: 'Gyro is switched off for this app just now.' };
    if (status === 403)
        return { message: 'You can’t rate outfits here.', hide: true };
    if (status === 400 || status === 413)
        return { message: 'Your stars couldn’t be saved.' };
    return { message: 'Your stars couldn’t be saved. Check your connection and try again.', reload: true };
}
/** Per client: the galleries listening, and until when gyro-svc asked for no more ratings (epoch ms). */
const channels = new WeakMap();
function channel(client) {
    let found = channels.get(client);
    if (!found) {
        found = { listeners: new Set(), waitUntil: 0 };
        channels.set(client, found);
    }
    return found;
}
/**
 * Hear every rating saved through `client`, so Settings and the chat's pickers show the same stars without reading the
 * gallery again. In memory only: never an event on `window`, never sent anywhere. Returns the way to stop listening.
 */
export function listenForMascotRatings(client, listener) {
    const { listeners } = channel(client);
    listeners.add(listener);
    return () => { listeners.delete(listener); };
}
/** Tell every gallery of `client`, the one that saved it included, the answer to a rating. */
export function shareMascotRating(client, answer) {
    for (const listener of [...channel(client).listeners])
        listener(answer);
}
/** gyro-svc asked for no more ratings for `waitMs` (a 429): every gallery of `client` waits. */
export function holdMascotRatings(client, waitMs, now = Date.now()) {
    const found = channel(client);
    found.waitUntil = Math.max(found.waitUntil, now + waitMs);
}
/** How long every gallery of `client` still waits before it sends another rating, in ms (0: none). */
export function mascotRatingWaitMs(client, now = Date.now()) {
    return Math.max(0, (channels.get(client)?.waitUntil ?? 0) - now);
}
//# sourceMappingURL=mascotRatings.js.map